import * as React from "react";
import { useState, useRef, useEffect } from "react";
import clx from "clsx";
import Box from "../box";
import Button from "../button";
import ClipboardCopyText from "../clipboard-copy-text";
import InstallButton from "../connect-modal-install-button";
import {
  statusLogo,
  disconnectedLogoFrame,
  disconnectedDesc,
  statusLogoImage,
  modalStatusContainer,
  connectingLogoFrame,
  connectingHeader,
  notExistLogoFrame,
  dangerText,
  errorDescription,
  statusLogoImageSvg,
  widthContainer,
  connectedInfo,
  desc,
  descMaxWidth,
  flexImg,
  bottomLink,
  copyText,
} from "./connect-modal-status.css";
import { baseTextStyles } from "../text/text.css";
import { bottomShadow } from "../shared/shared.css";
import { store } from "../../models/store";
import type { ThemeVariant } from "../../models/system.model";
import type { ConnectModalStatusProps } from "./connect-modal-status.types";

function ConnectModalStatus(props: ConnectModalStatusProps) {
  const cleanupRef = useRef<() => void>(null);
  const [theme, setTheme] = useState(() => "light");

  useEffect(() => {
    setTheme(store.getState().theme);
    cleanupRef.current = store.subscribe((newState) => {
      setTheme(newState.theme);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (typeof cleanupRef.current === "function") cleanupRef.current();
    };
  }, []);

  return (
    <div className={clx(modalStatusContainer[theme], props.className)}>
      {props.status === "Disconnected" ? (
        <>
          <Box marginBottom="$5" className={statusLogo}>
            <div className={disconnectedLogoFrame[theme]} />
            <div className={statusLogoImage}>
              <img
                src={props.wallet.logo}
                alt={props.wallet.name}
                className={flexImg}
              />
            </div>
          </Box>
          <p className={disconnectedDesc[theme]}>Wallet is disconnected</p>
          <div className={widthContainer}>
            <Button
              leftIcon="walletFilled"
              fluidWidth
              onClick={(event) => props.onConnect?.()}
              attributes={{
                marginBottom: "$3",
              }}
            >
              Connect wallet
            </Button>
          </div>
          {!!props.bottomLink ? (
            <div>
              <a
                target="_blank"
                rel="noreferrer"
                href={props.bottomLink}
                className={bottomLink}
              >
                Don't have a wallet?{" "}
              </a>
            </div>
          ) : null}
        </>
      ) : null}
      {props.status === "Connecting" ? (
        <>
          <Box marginBottom="$8" className={statusLogo}>
            <div className={connectingLogoFrame[theme]} />
            <div className={statusLogoImage}>
              <img
                src={props.wallet.logo}
                alt={props.wallet.name}
                className={flexImg}
              />
            </div>
          </Box>{" "}
          <p className={connectingHeader[theme]}>{props.contentHeader}</p>{" "}
          <Box
            as="p"
            fontSize="$sm"
            color="$body"
            fontWeight="$normal"
            textAlign="center"
            className={descMaxWidth}
          >
            {props.contentDesc}
          </Box>
        </>
      ) : null}
      {props.status === "Connected" ? (
        <>
          <Box marginBottom="$8" className={statusLogo}>
            {typeof props.connectedInfo.avatar === "string" ? (
              <div className={statusLogoImage}>
                <img
                  src={props.connectedInfo.avatar}
                  alt={props.connectedInfo.name}
                  className={flexImg}
                />
              </div>
            ) : null}
            {!!props.connectedInfo.avatar &&
            typeof props.connectedInfo.avatar !== "string" ? (
              <div className={statusLogoImageSvg}>
                {props.connectedInfo.avatar}
              </div>
            ) : null}
          </Box>{" "}
          <Box display="flex" alignItems="center" marginBottom="$5">
            <Box
              as="img"
              width="$8"
              height="$8"
              attributes={{ src: props.wallet.logo, alt: props.wallet.name }}
            />
            {!!props.connectedInfo?.name ? (
              <p className={connectedInfo[theme]}>{props.connectedInfo.name}</p>
            ) : null}
          </Box>{" "}
          <div className={widthContainer}>
            <Box maxWidth="$29" mx="auto">
              <ClipboardCopyText
                truncate="middle"
                text={props.connectedInfo.address}
                className={copyText}
              />
            </Box>
          </div>{" "}
          <div className={widthContainer}>
            <Box
              maxWidth="$29"
              mx="auto"
              attributes={{ "data-part-id": "ConnectModalStatus-disconnect" }}
            >
              <Button
                intent="primary"
                variant="solid"
                leftIcon="walletFilled"
                fluidWidth
                onClick={(event) => props.onDisconnect?.()}
              >
                {" "}
                Disconnect{" "}
              </Button>
            </Box>
          </div>
        </>
      ) : null}
      {props.status === "NotExist" ? (
        <>
          <Box marginBottom="$7" className={statusLogo}>
            <div className={notExistLogoFrame[theme]} />
            <div className={statusLogoImage}>
              <img
                src={props.wallet.logo}
                alt={props.wallet.name}
                className={flexImg}
              />
            </div>
          </Box>{" "}
          <p className={dangerText[theme]}>{props.contentHeader}</p>{" "}
          <p className={clx(baseTextStyles, desc[theme])}>
            {props.contentDesc}
          </p>{" "}
          <div className={widthContainer}>
            <Box mt="$7">
              <InstallButton
                fluidWidth
                onClick={(event) => props.onInstall?.()}
                disabled={!!props.disableInstall}
              >
                <Box
                  as="span"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <span>{props.installIcon}</span>
                  <Box as="span" marginLeft="$4">
                    {" "}
                    Install {props.wallet.prettyName ?? props.wallet.name}
                  </Box>
                </Box>
              </InstallButton>
            </Box>
          </div>
        </>
      ) : null}
      {props.status === "Rejected" ? (
        <>
          <Box marginBottom="$7" className={statusLogo}>
            <div className={notExistLogoFrame[theme]} />
            <div className={statusLogoImage}>
              <img
                src={props.wallet.logo}
                alt={props.wallet.name}
                className={flexImg}
              />
            </div>
          </Box>{" "}
          <p className={dangerText[theme]}>{props.contentHeader}</p>{" "}
          <p className={desc[theme]}>{props.contentDesc}</p>{" "}
          <div className={widthContainer}>
            <Button
              leftIcon="walletFilled"
              fluidWidth
              onClick={(event) => props.onConnect?.()}
              attributes={{ marginBottom: "$3" }}
            >
              {" "}
              Reconnect{" "}
            </Button>
          </div>
        </>
      ) : null}
      {props.status === "Error" ? (
        <>
          <Box marginBottom="$7" className={statusLogo}>
            <div className={notExistLogoFrame[theme]} />
            <div className={statusLogoImage}>
              <img
                src={props.wallet.logo}
                alt={props.wallet.name}
                className={flexImg}
              />
            </div>
          </Box>{" "}
          <p className={dangerText[theme]}>{props.contentHeader}</p>{" "}
          <Box position="relative">
            <div className={errorDescription}>
              <p className={desc[theme]}>{props.contentDesc}</p>
            </div>
            <div className={bottomShadow[theme]} />
          </Box>{" "}
          <div className={widthContainer}>
            <Button
              leftIcon="walletFilled"
              fluidWidth
              onClick={(event) => props.onChangeWallet?.()}
              attributes={{ marginBottom: "$3" }}
            >
              {" "}
              Change wallet{" "}
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}
export default ConnectModalStatus;
